/* Eastwood theme — no build step. Custom elements, loaded with defer. */

const theme = window.theme || {};

/* Helpers ------------------------------------------------------------------ */
const debounce = (fn, wait = 250) => {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
};

const formatMoney = (cents, format = theme.moneyFormat || '${{amount}}') => {
  const value = Number(cents) / 100;
  const withDelimiters = (n, decimals, thousands = ',', decimal = '.') => {
    const [int, frac] = n.toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return withDelimiters(value, 0);
      case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
      default: return withDelimiters(value, 2);
    }
  });
};

const fetchSection = async (url, sectionId) => {
  const sep = url.includes('?') ? '&' : '?';
  const res = await fetch(`${url}${sep}section_id=${sectionId}`);
  const html = await res.text();
  return new DOMParser().parseFromString(html, 'text/html');
};

const recentlyViewed = {
  key: 'eastwood-recent',
  get() {
    try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch (_) { return []; }
  },
  add(handle) {
    const list = [handle, ...this.get().filter((h) => h !== handle)].slice(0, 12);
    try { localStorage.setItem(this.key, JSON.stringify(list)); } catch (_) { /* storage off */ }
  },
};

const lockScroll = (lock) => document.documentElement.classList.toggle('menu-open', lock);

/* Drawer base ------------------------------------------------------------- */
class SideDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.drawer__panel');
    this.content = this.querySelector('[data-drawer-content]') || this.querySelector('.drawer__content');
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]')) this.close();
    });
    this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
  }

  open(trigger) {
    this.trigger = trigger || document.activeElement;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    lockScroll(true);
    requestAnimationFrame(() => this.panel?.focus());
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.drawer.is-open, .search-modal.is-open')) lockScroll(false);
    this.trigger?.focus?.();
  }

  setContent(nodeOrHtml) {
    if (!this.content) return;
    this.content.innerHTML = '';
    if (typeof nodeOrHtml === 'string') this.content.innerHTML = nodeOrHtml;
    else this.content.append(nodeOrHtml);
  }
}
customElements.define('side-drawer', SideDrawer);

/* Cart drawer --------------------------------------------------------------- */
class CartDrawer extends SideDrawer {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', (e) => {
      const qtyBtn = e.target.closest('[data-qty]');
      if (qtyBtn) {
        const input = qtyBtn.parentElement.querySelector('[data-line-qty]');
        const next = Math.max(0, Number(input.value) + Number(qtyBtn.dataset.qty));
        this.change(input.dataset.lineQty, next);
      }
      const remove = e.target.closest('[data-line-remove]');
      if (remove) this.change(remove.dataset.lineRemove, 0);
    });
    this.addEventListener('change', (e) => {
      const input = e.target.closest('[data-line-qty]');
      if (input) this.change(input.dataset.lineQty, Math.max(0, Number(input.value)));
      const note = e.target.closest('[data-cart-note]');
      if (note) {
        fetch(`${theme.routes.cart}/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: note.value }),
        });
      }
    });
  }

  async change(line, quantity) {
    this.classList.add('is-loading');
    try {
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity, sections: ['cart-drawer'] }),
      });
      const data = await res.json();
      if (data.sections) this.render(data.sections['cart-drawer']);
      updateCartCount(data.item_count);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  async refresh() {
    const res = await fetch(`${theme.routes.root}?sections=cart-drawer`);
    const data = await res.json();
    this.render(data['cart-drawer']);
    const cart = await (await fetch(`${theme.routes.cart}.js`)).json();
    updateCartCount(cart.item_count);
  }

  render(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.getElementById('CartDrawerContent');
    if (next) this.querySelector('#CartDrawerContent').replaceWith(next);
  }
}
customElements.define('cart-drawer', CartDrawer);

const updateCartCount = (count) => {
  if (typeof count !== 'number') return;
  document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = `(${count})`; });
};

const openCart = async () => {
  const drawer = document.getElementById('CartDrawer');
  if (!drawer || theme.cartType === 'page') { window.location.href = theme.routes.cart; return; }
  await drawer.refresh();
  drawer.open();
};

const addToCart = async (formData) => {
  const res = await fetch(`${theme.routes.cartAdd}.js`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok || data.status) throw new Error(data.description || data.message || 'Error');
  return data;
};

document.addEventListener('click', (e) => {
  const cartLink = e.target.closest('[data-cart-open]');
  if (cartLink && theme.cartType !== 'page') {
    e.preventDefault();
    openCart();
  }
});

/* Announcement ---------------------------------------------------------------- */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    const key = `announcement-closed-${this.dataset.id}`;
    try { if (sessionStorage.getItem(key)) { this.closest('.shopify-section')?.remove(); return; } } catch (_) { /* storage blocked */ }
    this.querySelector('[data-announcement-close]')?.addEventListener('click', () => {
      try { sessionStorage.setItem(key, '1'); } catch (_) { /* storage blocked */ }
      this.closest('.shopify-section')?.remove() || this.remove();
    });
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* Header ------------------------------------------------------------------------ */
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.lastY = window.scrollY;
    const section = this.closest('.section-header') || this;
    new ResizeObserver(() => {
      if (this.classList.contains('is-open')) return;
      document.documentElement.style.setProperty('--header-h', `${section.offsetHeight}px`);
    }).observe(section);
    this.headroom = this.dataset.headroom === 'true';
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();

    this.toggles = [...this.querySelectorAll('[data-mega-toggle]')];
    this.toggles.forEach((toggle) => toggle.addEventListener('click', (e) => {
      e.preventDefault();
      const isActive = toggle.getAttribute('aria-expanded') === 'true';
      this.toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      if (isActive) { this.closeMega(); return; }
      toggle.setAttribute('aria-expanded', 'true');
      const index = toggle.closest('[data-index]')?.dataset.index;
      this.querySelectorAll('.header__sub[data-parent]').forEach((list) => list.classList.toggle('is-active', list.dataset.parent === index));
      this.showTertiary(null);
      const desktop = this.querySelector('.header__desktop');
      const logoRow = this.querySelector('.header__logo-row');
      const nav = this.querySelector('.header__grid');
      const top = logoRow ? logoRow.offsetTop : nav.offsetTop + nav.offsetHeight + 40;
      desktop?.style.setProperty('--mega-top', `${top}px`);
      this.classList.add('is-open');
    }));

    this.querySelector('.header__logo')?.addEventListener('mouseenter', () => {
      if (this.classList.contains('is-condensed')) this.classList.add('is-peek');
    });
    this.addEventListener('mouseleave', () => this.classList.remove('is-peek'));

    this.querySelectorAll('[data-tertiary]').forEach((link) => link.addEventListener('mouseenter', () => this.showTertiary(link)));
    this.querySelectorAll('.header__sub:not(.header__sub--tertiary) a:not([data-tertiary])').forEach((link) => link.addEventListener('mouseenter', () => this.showTertiary(null)));

    document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.closeMega(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.closeMega(); this.toggleMobile(false); } });

    this.mobileToggle = this.querySelector('[data-mobile-menu-toggle]');
    this.mobileMenu = this.querySelector('.mobile-menu');
    this.mobileToggle?.addEventListener('click', () => this.toggleMobile(this.mobileMenu.hidden));

    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-search-open]')) {
        this.toggleMobile(false);
        document.getElementById('SearchModal')?.open();
      }
    });
  }

  disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }

  onScroll() {
    const y = window.scrollY;
    this.classList.toggle('is-scrolled', y > 10);
    if (this.condense()) { this.lastY = y; return; }
    if (this.headroom && !this.classList.contains('is-open') && (this.mobileMenu?.hidden ?? true)) {
      const goingDown = y > this.lastY && y > this.offsetHeight;
      this.classList.toggle('is-hidden', goingDown);
    }
    this.lastY = y;
  }

  /* Desktop pages with a logo row: keep only the logo floating once scrolled. */
  condense() {
    const logoRow = this.querySelector('.header__logo-row');
    const desktop = window.matchMedia('(min-width: 1024px)').matches;
    if (!logoRow || !desktop) { this.classList.remove('is-condensed'); return false; }
    const condensed = window.scrollY > 50;
    if (condensed && !this.classList.contains('is-condensed')) {
      this.closeMega();
      this.style.setProperty('--condense-offset', `${logoRow.offsetTop - 27}px`);
    }
    this.classList.toggle('is-condensed', condensed);
    if (!condensed) this.classList.remove('is-peek');
    this.classList.remove('is-hidden');
    return true;
  }

  closeMega() {
    this.classList.remove('is-open');
    this.toggles?.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.showTertiary(null);
  }

  showTertiary(link) {
    this.querySelectorAll('[data-tertiary-list]').forEach((list) => {
      list.hidden = !link || list.dataset.tertiaryList !== link.dataset.tertiary;
    });
    this.querySelectorAll('[data-tertiary]').forEach((a) => a.classList.toggle('is-active', a === link));
  }

  toggleMobile(open) {
    if (!this.mobileMenu) return;
    if (open) this.mobileMenu.style.top = `${this.querySelector('.header__mobile').getBoundingClientRect().bottom}px`;
    this.mobileMenu.hidden = !open;
    this.mobileToggle?.setAttribute('aria-expanded', String(open));
    this.classList.remove('is-hidden');
    lockScroll(open);
  }
}
customElements.define('sticky-header', StickyHeader);

/* Search ---------------------------------------------------------------------------- */
class SearchModal extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    this.addEventListener('click', (e) => { if (e.target.closest('[data-search-close]')) this.close(); });
    this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    this.input.addEventListener('input', debounce(() => this.search(), 300));
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    lockScroll(true);
    setTimeout(() => this.input.focus(), 50);
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    lockScroll(false);
  }

  async search() {
    const q = this.input.value.trim();
    if (q.length < 2) { this.results.innerHTML = ''; this.classList.remove('has-results'); return; }
    const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page&resources[limit]=4&section_id=predictive-search`;
    const html = await (await fetch(url)).text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.getElementById('predictive-search-results');
    this.results.innerHTML = content ? content.outerHTML : '';
    this.classList.toggle('has-results', Boolean(content));
  }
}
customElements.define('search-modal', SearchModal);

/* Hero showcase: active item follows the media in view --------------------------- */
class HeroShowcase extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.hero-showcase__item')];
    this.media = [...this.querySelectorAll('.hero-showcase__media-item')];
    this.update = this.update.bind(this);
    window.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update);
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.update);
    window.removeEventListener('resize', this.update);
  }

  update() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      const top = this.getBoundingClientRect().top;
      const vh = window.innerHeight;
      const index = Math.min(this.media.length - 1, Math.max(0, Math.floor((-top + vh / 2) / vh)));
      if (index === this.active) return;
      this.active = index;
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
    });
  }
}
customElements.define('hero-showcase', HeroShowcase);

/* Collection: dropdowns + grid view ----------------------------------------------- */
class CollectionView extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('.product-grid');
    this.storageKey = 'collection-view';
    let stored = null;
    try { stored = localStorage.getItem(this.storageKey); } catch (_) { /* storage blocked */ }
    this.setView(stored || this.dataset.defaultView, false);

    this.querySelectorAll('[data-view]').forEach((btn) => {
      if (btn.tagName !== 'BUTTON') return;
      btn.addEventListener('click', () => {
        this.setView(btn.dataset.view, true);
        btn.closest('details')?.removeAttribute('open');
      });
    });

    this.sentinel = this.querySelector('[data-infinite-sentinel]');
    if (this.sentinel && this.grid?.dataset.nextUrl) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
      }, { rootMargin: '0px 0px 800px 0px' });
      this.observer.observe(this.sentinel);
    }

    const dropdowns = [...this.querySelectorAll('[data-dropdown]')];
    dropdowns.forEach((d) => d.addEventListener('toggle', () => {
      if (d.open) dropdowns.forEach((o) => { if (o !== d) o.removeAttribute('open'); });
    }));
    document.addEventListener('click', (e) => {
      dropdowns.forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
    });
  }

  async loadMore() {
    const url = this.grid.dataset.nextUrl;
    if (!url || this.loading) return;
    this.loading = true;
    this.grid.classList.add('is-loading');
    const loader = this.querySelector('[data-infinite-loader]');
    if (loader) loader.hidden = false;
    try {
      const doc = await fetchSection(url, this.dataset.sectionId);
      const nextGrid = doc.querySelector('.product-grid');
      if (nextGrid) {
        this.grid.append(...nextGrid.querySelectorAll(':scope > .product-grid__item'));
        if (nextGrid.dataset.nextUrl) this.grid.dataset.nextUrl = nextGrid.dataset.nextUrl;
        else { delete this.grid.dataset.nextUrl; this.observer?.disconnect(); }
      } else {
        delete this.grid.dataset.nextUrl; this.observer?.disconnect();
      }
      document.dispatchEvent(new CustomEvent('wishlist:refresh'));
    } finally {
      this.loading = false;
      this.grid.classList.remove('is-loading');
      const loader = this.querySelector('[data-infinite-loader]');
      if (loader) loader.hidden = true;
    }
  }

  setView(view, persist) {
    if (!this.grid || !view) return;
    this.grid.dataset.view = view;
    this.querySelectorAll('button[data-view]').forEach((b) => b.classList.toggle('is-active', b.dataset.view === view));
    if (persist) { try { localStorage.setItem(this.storageKey, view); } catch (_) { /* storage blocked */ } }
  }
}
customElements.define('collection-view', CollectionView);

/* Read more --------------------------------------------------------------------------- */
class ReadMore extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('.read-more__content');
    this.toggle = this.querySelector('.read-more__toggle');
    this.classList.add('is-clamped');
    requestAnimationFrame(() => {
      if (this.content.scrollHeight > this.content.clientHeight + 2) this.toggle.hidden = false;
    });
    this.toggle.addEventListener('click', () => {
      const clamped = this.classList.toggle('is-clamped');
      this.toggle.textContent = clamped ? (theme.strings.readMore || 'Read more') : (theme.strings.readLess || 'Read less');
    });
  }
}
customElements.define('read-more', ReadMore);

/* Share --------------------------------------------------------------------------------- */
class ShareButton extends HTMLElement {
  connectedCallback() {
    const btn = this.querySelector('button');
    btn.addEventListener('click', async () => {
      const { shareUrl: url, shareTitle: title } = btn.dataset;
      if (navigator.share) { try { await navigator.share({ url, title }); } catch (_) { /* dismissed */ } return; }
      await navigator.clipboard?.writeText(url);
      const note = this.querySelector('.share__copied');
      if (note) { note.hidden = false; setTimeout(() => { note.hidden = true; }, 2000); }
    });
  }
}
customElements.define('share-button', ShareButton);

/* Option drawer shared by product page and drawer links ------------------------------- */

/* Turn each heading in rich text into a disclosure row holding the content that follows it */
const buildAccordions = (root) => {
  root.querySelectorAll('[data-accordion-content]').forEach((container) => {
    const headings = [...container.querySelectorAll(':scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6')];
    if (!headings.length) return;
    const list = document.createElement('div');
    list.className = 'accordion';
    headings[0].before(list);
    headings.forEach((heading) => {
      const details = document.createElement('details');
      details.className = 'accordion__item';
      const summary = document.createElement('summary');
      summary.innerHTML = `<span>${heading.innerHTML}</span><svg class="icon" aria-hidden="true" width="9" height="6" viewBox="0 0 9 6" fill="none"><path d="M.5 1l4 4 4-4" stroke="currentColor"/></svg>`;
      const content = document.createElement('div');
      content.className = 'accordion__content';
      let next = heading.nextElementSibling;
      while (next && !/^H[1-6]$/.test(next.tagName)) {
        const current = next;
        next = next.nextElementSibling;
        content.append(current);
      }
      details.append(summary, content);
      list.append(details);
      heading.remove();
    });
  });
};

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-drawer-template]');
  if (!trigger) return;
  const tpl = document.getElementById(trigger.dataset.drawerTemplate);
  const drawer = document.getElementById(trigger.dataset.drawerOpen);
  if (!tpl || !drawer) return;
  const fragment = tpl.content.cloneNode(true);
  const wrap = document.createElement('div');
  wrap.append(fragment);
  buildAccordions(wrap);
  drawer.setContent(wrap);
  drawer.open(trigger);
});

/* Variant selection logic shared by product page + quick add -------------------------- */
class VariantState {
  constructor(product, initialVariant) {
    this.product = product;
    this.selected = product.options.map((_, i) => (initialVariant ? initialVariant.options[i] : null));
  }

  get complete() { return this.selected.every((v) => v !== null); }

  get variant() {
    if (!this.complete) return null;
    return this.product.variants.find((v) => v.options.every((o, i) => o === this.selected[i])) || null;
  }

  isAvailable(index, value) {
    return this.product.variants.some((v) => v.available && v.options[index] === value
      && v.options.every((o, i) => i === index || this.selected[i] === null || this.selected[i] === o));
  }

  values(index) {
    return [...new Set(this.product.variants.map((v) => v.options[index]))];
  }
}

/* Product page ------------------------------------------------------------------------------ */
class ProductPage extends HTMLElement {
  connectedCallback() {
    this.product = JSON.parse(this.querySelector('[data-product-json]').textContent);
    recentlyViewed.add(this.product.handle);
    this.setupGallery();
    const params = new URLSearchParams(window.location.search);
    const initial = this.product.variants.length === 1
      ? this.product.variants[0]
      : this.product.variants.find((v) => String(v.id) === params.get('variant'));
    this.state = new VariantState(this.product, initial);

    this.form = this.querySelector('[data-product-form]');
    this.idInput = this.querySelector('[data-variant-id]');
    this.button = this.querySelector('[data-add-button]');
    this.error = this.querySelector('.product-form__error');
    this.rows = [...this.querySelectorAll('.variant-picker__row')];

    this.pickerStyle = this.querySelector('variant-picker')?.dataset.pickerStyle;
    this.configurator = this.querySelector('[data-configurator]');

    this.rows.forEach((row) => {
      row.addEventListener('click', () => {
        const index = Number(row.dataset.optionIndex);
        if (this.configurator) this.openConfigurator(index, row);
        else this.toggleDropdown(index);
      });
    });
    this.querySelectorAll('[data-option-pick]').forEach((btn) => btn.addEventListener('click', () => {
      const index = Number(btn.closest('.variant-picker__group').querySelector('[data-option-index]').dataset.optionIndex);
      this.pick(index, btn.dataset.optionPick);
      this.toggleDropdown(index, false);
    }));
    if (this.configurator) this.setupConfigurator();
    this.syncRows();

    this.form?.addEventListener('submit', (e) => this.onSubmit(e));
    this.querySelector('[data-configure]')?.addEventListener('click', (e) => {
      const first = this.state.selected.findIndex((v) => v === null);
      this.openOption(first === -1 ? 0 : first, e.currentTarget);
    });
    this.querySelectorAll('[data-qty-step]').forEach((btn) => btn.addEventListener('click', () => {
      this.setQty(this.qty + Number(btn.dataset.qtyStep));
    }));
    this.querySelector('[data-qty-input]')?.addEventListener('change', () => this.setQty(this.qty));

    this.enquiry = this.querySelector('#EnquiryDrawer');
    this.querySelectorAll('[data-enquiry-open]').forEach((btn) => btn.addEventListener('click', () => this.enquiry?.open(btn)));
    if (this.querySelector('[data-enquiry-autoopen]')) requestAnimationFrame(() => this.enquiry?.open());
  }

  disconnectedCallback() {
    // Theme editor re-renders the section; drop the selector that was moved to <body>
    this.configurator?.remove();
  }

  openOption(index, trigger) {
    if (this.configurator) this.openConfigurator(index, trigger);
    else this.toggleDropdown(index, true);
  }

  pick(index, value) {
    this.state.selected[index] = value;
    this.syncRows();
    if (this.state.complete) this.onVariantChange();
  }

  get qty() {
    const input = this.querySelector('[data-qty-input]');
    return input ? Math.max(1, Number(input.value) || 1) : (this.storedQty || 1);
  }

  setQty(value) {
    this.storedQty = Math.max(1, value);
    const input = this.querySelector('[data-qty-input]');
    if (input) input.value = this.storedQty;
    this.syncConfigurator();
  }

  optionName(index) {
    const option = this.product.options[index];
    return typeof option === 'string' ? option : option.name;
  }

  /* Price for the current selection: exact when complete, otherwise the lowest matching price */
  priceText() {
    const variant = this.state.variant;
    if (variant) return formatMoney(variant.price * this.qty);
    const matches = this.product.variants.filter((v) => v.options.every((o, i) => this.state.selected[i] === null || this.state.selected[i] === o));
    const min = Math.min(...(matches.length ? matches : this.product.variants).map((v) => v.price));
    return `${theme.strings.from || 'From'} ${formatMoney(min * this.qty)}`;
  }

  syncRows() {
    const makeSelection = theme.strings.makeSelection || '';
    this.rows.forEach((row) => {
      const i = Number(row.dataset.optionIndex);
      const value = this.state.selected[i];
      row.querySelector('[data-option-value]').textContent = value ?? makeSelection;
      row.classList.toggle('is-set', value !== null);
      row.closest('.variant-picker__group')?.querySelectorAll('[data-option-pick]').forEach((btn) => {
        btn.setAttribute('aria-pressed', String(btn.dataset.optionPick === value));
        btn.classList.toggle('is-unavailable', !this.state.isAvailable(i, btn.dataset.optionPick));
      });
    });
    this.syncConfigurator();
  }

  toggleDropdown(index, force) {
    this.rows.forEach((row, i) => {
      const list = row.closest('.variant-picker__group')?.querySelector('.variant-picker__list');
      if (!list) return;
      const open = i === index && (force ?? list.hidden);
      list.hidden = !open;
      row.setAttribute('aria-expanded', String(open));
    });
  }

  /* Full-screen variant selector: selection summary on the left, one step per option on the right */
  setupConfigurator() {
    const c = this.configurator;
    // The buy column is sticky, which traps z-index; lift the selector to <body> so it covers the header
    document.body.append(c);
    this.steps = [...c.querySelectorAll('[data-config-step]')];
    c.querySelector('[data-config-close]').addEventListener('click', () => this.closeConfigurator());
    c.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeConfigurator(); });
    this.steps.forEach((step, i) => {
      step.querySelector('[data-config-step-toggle]').addEventListener('click', () => this.openStep(i, true));
    });
    c.querySelectorAll('[data-config-row]').forEach((row) => row.addEventListener('click', () => this.openStep(Number(row.dataset.configRow))));
    c.querySelectorAll('[data-config-pick]').forEach((input) => input.addEventListener('change', () => {
      this.pick(Number(input.dataset.configPick), input.value);
      clearTimeout(this.advanceTimer);
      this.advanceTimer = setTimeout(() => {
        const next = this.state.selected.findIndex((v) => v === null);
        this.openStep(next === -1 ? this.steps.length - 1 : next);
      }, 250);
    }));
    c.querySelectorAll('[data-config-qty-step]').forEach((btn) => btn.addEventListener('click', () => {
      this.setQty(this.qty + Number(btn.dataset.configQtyStep));
    }));
    c.querySelectorAll('[data-config-enquire]').forEach((btn) => btn.addEventListener('click', () => {
      this.closeConfigurator();
      this.enquiry?.open(this.configTrigger);
    }));
    c.querySelector('[data-config-add]').addEventListener('click', () => this.configAdd());
  }

  openConfigurator(index, trigger) {
    this.configTrigger = trigger;
    this.syncRows();
    this.configurator.classList.add('is-open');
    lockScroll(true);
    this.openStep(index === -1 ? this.steps.length - 1 : index);
    this.configurator.querySelector('[data-config-close]').focus({ preventScroll: true });
  }

  closeConfigurator() {
    if (!this.configurator?.classList.contains('is-open')) return;
    this.configurator.classList.remove('is-open');
    lockScroll(false);
    this.configTrigger?.focus({ preventScroll: true });
  }

  openStep(index, toggle = false) {
    this.steps.forEach((step, i) => {
      const head = step.querySelector('[data-config-step-toggle]');
      const open = i === index && !(toggle && head.getAttribute('aria-expanded') === 'true');
      head.setAttribute('aria-expanded', String(open));
      step.querySelector('.configurator__step-body').hidden = !open;
      step.classList.toggle('is-open', open);
    });
  }

  syncConfigurator() {
    const c = this.configurator;
    if (!c) return;
    const makeSelection = theme.strings.makeSelection || '';
    c.querySelectorAll('[data-config-row]').forEach((row) => {
      const value = this.state.selected[Number(row.dataset.configRow)];
      row.querySelector('[data-config-row-value]').textContent = value ?? makeSelection;
      row.classList.toggle('is-set', value !== null);
    });
    c.querySelectorAll('[data-config-pick]').forEach((input) => {
      const i = Number(input.dataset.configPick);
      input.checked = this.state.selected[i] === input.value;
      input.closest('.configurator__value').classList.toggle('is-unavailable', !this.state.isAvailable(i, input.value));
    });
    c.querySelector('[data-config-qty]').textContent = this.qty;
    const price = this.priceText();
    // innerHTML: currency apps wrap formatted prices in their own markup
    c.querySelector('[data-config-subtotal]').innerHTML = price;
    c.querySelector('[data-config-summary-price]').innerHTML = price;
    const review = c.querySelector('[data-config-review]');
    review.innerHTML = '';
    this.state.selected.forEach((value, i) => {
      const row = document.createElement('div');
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      dt.textContent = this.optionName(i);
      dd.textContent = value ?? makeSelection;
      row.append(dt, dd);
      review.append(row);
    });
    const variant = this.state.variant;
    const add = c.querySelector('[data-config-add]');
    add.disabled = Boolean(this.state.complete && (!variant || !variant.available));
    add.textContent = !this.state.complete || variant?.available ? theme.strings.addToCart
      : (variant ? theme.strings.soldOut : theme.strings.unavailable);
  }

  configAdd() {
    const first = this.state.selected.findIndex((v) => v === null);
    if (first !== -1) { this.openStep(first); return; }
    if (!this.state.variant?.available) return;
    let qty = this.form.querySelector('[name="quantity"]');
    if (!qty) {
      qty = document.createElement('input');
      qty.type = 'hidden';
      qty.name = 'quantity';
      this.form.append(qty);
    }
    qty.value = this.qty;
    this.idInput.value = this.state.variant.id;
    this.idInput.disabled = false;
    this.closeConfigurator();
    this.form.requestSubmit();
  }

  async onVariantChange() {
    const variant = this.state.variant;
    this.error.hidden = true;
    if (!variant) {
      this.idInput.disabled = true;
      this.button.disabled = true;
      this.button.textContent = theme.strings.unavailable;
      return;
    }
    this.idInput.value = variant.id;
    this.idInput.disabled = false;
    this.querySelectorAll('[data-enquiry-variant]').forEach((el) => { el.textContent = variant.title; });
    this.querySelectorAll('[data-enquiry-variant-input]').forEach((el) => { el.value = variant.title; });
    this.button.disabled = !variant.available;
    this.button.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;

    const url = `${this.dataset.url}?variant=${variant.id}`;
    window.history.replaceState({}, '', url);

    const doc = await fetchSection(url, this.dataset.sectionId);
    const nextPrice = doc.querySelector('[data-price]');
    const price = this.querySelector('[data-price]');
    if (nextPrice && price) price.innerHTML = nextPrice.innerHTML;

    if (variant.featured_media) {
      const media = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      if (media) {
        if (window.matchMedia('(min-width: 1024px)').matches) media.scrollIntoView({ behavior: 'smooth', block: 'center' });
        else media.parentElement.scrollTo({ left: media.offsetLeft - media.parentElement.offsetLeft, behavior: 'smooth' });
      }
    }
  }

  setupGallery() {
    const track = this.querySelector('.product__media');
    this.querySelectorAll('[data-media-step]').forEach((btn) => btn.addEventListener('click', () => {
      const item = btn.closest('.product__media-item');
      const target = Number(btn.dataset.mediaStep) > 0 ? item.nextElementSibling : item.previousElementSibling;
      if (!target) return;
      if (window.matchMedia('(min-width: 1024px)').matches) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      else track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    }));

    const modal = this.querySelector('[data-gallery-modal]');
    if (!modal) return;
    let returnY = 0;
    let opener = null;
    const close = () => {
      if (modal.hidden) return;
      modal.hidden = true;
      lockScroll(false);
      window.scrollTo({ top: returnY, behavior: 'instant' });
      opener?.focus({ preventScroll: true });
    };
    modal.querySelector('[data-gallery-close]').addEventListener('click', close);
    modal.querySelectorAll('[data-gallery-item]').forEach((item) => item.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
    this.querySelectorAll('[data-gallery-open]').forEach((btn) => btn.addEventListener('click', () => {
      opener = btn;
      returnY = window.scrollY;
      // Back to the top so the full header and logo show above the gallery, as on the target
      window.scrollTo({ top: 0, behavior: 'instant' });
      window.dispatchEvent(new Event('scroll'));
      const header = document.querySelector('.section-header');
      modal.style.setProperty('--gallery-top', `${header ? Math.round(header.getBoundingClientRect().bottom) : 0}px`);
      modal.hidden = false;
      lockScroll(true);
      const item = modal.querySelector(`[data-gallery-item="${btn.dataset.galleryOpen}"]`);
      const list = modal.querySelector('.gallery-modal__list');
      modal.scrollTop = item && item !== list.firstElementChild ? item.offsetTop : 0;
      modal.focus({ preventScroll: true });
    }));
  }

  async onSubmit(e) {
    e.preventDefault();
    if (!this.state.complete) {
      const first = this.state.selected.findIndex((v) => v === null);
      this.openOption(first, this.button);
      return;
    }
    if (!this.state.variant) return;
    this.button.setAttribute('aria-disabled', 'true');
    try {
      await addToCart(new FormData(this.form));
      await openCart();
    } catch (err) {
      this.error.textContent = err.message;
      this.error.hidden = false;
    } finally {
      this.button.removeAttribute('aria-disabled');
    }
  }
}
customElements.define('product-page', ProductPage);

/* Quick add ---------------------------------------------------------------------------------- */
document.addEventListener('click', async (e) => {
  const trigger = e.target.closest('[data-quick-add]');
  if (!trigger) return;
  e.preventDefault();
  const productUrl = trigger.dataset.quickAdd.split('?')[0];
  const product = await (await fetch(`${productUrl}.js`)).json();

  if (product.variants.length === 1) {
    const fd = new FormData();
    fd.append('id', product.variants[0].id);
    fd.append('quantity', '1');
    try { await addToCart(fd); await openCart(); } catch (err) { window.alert(err.message); }
    return;
  }

  const drawer = document.getElementById('QuickAdd');
  const state = new VariantState(product, product.variants.find((v) => v.available) || product.variants[0]);

  const wrap = document.createElement('form');
  wrap.className = 'quick-add';
  const image = product.featured_image ? `<img class="quick-add__image" src="${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=360" alt="" width="178" height="178">` : '';
  wrap.innerHTML = `
    <div class="quick-add__head">${image}
      <div><a href="${productUrl}">${product.title}</a><div>${product.vendor || ''}</div></div>
      <p class="quick-add__price" data-qa-price></p>
    </div>
    <div class="quick-add__options"></div>
    <div class="quick-add__qty">
      <span class="form-label">${theme.strings.quantity || 'Quantity'}</span>
      <quantity-input class="quantity">
        <button type="button" data-step="-1" aria-label="-">−</button>
        <input type="number" name="quantity" value="1" min="1">
        <button type="button" data-step="1" aria-label="+">+</button>
      </quantity-input>
    </div>
    <button type="submit" class="button button--primary" data-qa-submit></button>
    <p class="product-form__error" role="alert" hidden></p>`;

  const optionsWrap = wrap.querySelector('.quick-add__options');
  product.options.forEach((option, index) => {
    const label = document.createElement('label');
    label.className = 'quick-add__option';
    label.innerHTML = `<span class="form-label">${option.name}</span>`;
    const select = document.createElement('select');
    select.className = 'quick-add__select';
    state.values(index).forEach((value) => {
      const opt = new Option(value, value, false, state.selected[index] === value);
      select.add(opt);
    });
    select.addEventListener('change', () => { state.selected[index] = select.value; refresh(); });
    label.append(select);
    optionsWrap.append(label);
  });

  const submit = wrap.querySelector('[data-qa-submit]');
  const priceEl = wrap.querySelector('[data-qa-price]');
  const errorEl = wrap.querySelector('.product-form__error');
  const refresh = () => {
    const v = state.variant;
    priceEl.innerHTML = v ? formatMoney(v.price) : '';
    submit.disabled = !v || !v.available;
    submit.textContent = !v ? theme.strings.unavailable : (v.available ? theme.strings.addToCart : theme.strings.soldOut);
  };
  refresh();

  wrap.addEventListener('click', (ev) => {
    const step = ev.target.closest('[data-step]');
    if (!step) return;
    const input = wrap.querySelector('input[name="quantity"]');
    input.value = Math.max(1, Number(input.value) + Number(step.dataset.step));
  });
  wrap.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const v = state.variant;
    if (!v) return;
    const fd = new FormData();
    fd.append('id', v.id);
    fd.append('quantity', wrap.querySelector('input[name="quantity"]').value);
    try {
      await addToCart(fd);
      drawer.close();
      await openCart();
    } catch (err) {
      errorEl.textContent = err.message;
      errorEl.hidden = false;
    }
  });

  drawer.setContent(wrap);
  drawer.open(trigger);
});

/* Footer localization auto-submit -------------------------------------------------------------- */
document.addEventListener('change', (e) => {
  const select = e.target.closest('select[data-autosubmit]');
  if (select) select.form.submit();
});

/* Wishlist (stored per browser) ---------------------------------------------------- */
const wishlist = {
  key: 'eastwood-wishlist',
  get() {
    try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch (_) { return []; }
  },
  set(handles) {
    try { localStorage.setItem(this.key, JSON.stringify(handles)); } catch (_) { /* storage blocked */ }
    document.dispatchEvent(new CustomEvent('wishlist:refresh'));
  },
  has(handle) { return this.get().includes(handle); },
  toggle(handle) {
    const list = this.get();
    this.set(list.includes(handle) ? list.filter((h) => h !== handle) : [...list, handle]);
  },
};

const syncWishlistUI = () => {
  const list = wishlist.get();
  document.querySelectorAll('[data-wishlist-count]').forEach((el) => { el.textContent = `(${list.length})`; });
  document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
    const active = list.includes(btn.dataset.wishlistToggle);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', String(active));
    btn.textContent = active ? btn.dataset.labelRemove : btn.dataset.labelAdd;
  });
};

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-wishlist-toggle]');
  if (btn) { e.preventDefault(); wishlist.toggle(btn.dataset.wishlistToggle); }
});
document.addEventListener('wishlist:refresh', syncWishlistUI);
syncWishlistUI();

class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.status = this.querySelector('[data-wishlist-status]');
    const shared = new URLSearchParams(window.location.search).get('items');
    this.shared = shared ? shared.split(',').filter(Boolean) : null;
    this.render();
    document.addEventListener('wishlist:refresh', () => { if (!this.shared) this.render(); });
    this.querySelector('[data-wishlist-add-all]')?.addEventListener('click', () => this.addAll());
    this.querySelector('[data-wishlist-share]')?.addEventListener('click', () => this.share());
    this.querySelector('[data-wishlist-print]')?.addEventListener('click', () => window.print());
  }

  get handles() { return this.shared || wishlist.get(); }

  say(message) {
    this.status.textContent = message;
    this.status.hidden = false;
    clearTimeout(this.statusTimer);
    this.statusTimer = setTimeout(() => { this.status.hidden = true; }, 4000);
  }

  async fetchProducts() {
    const root = theme.routes.root.replace(/\/$/, '');
    return (await Promise.all(this.handles.map(async (handle) => {
      const res = await fetch(`${root}/products/${handle}.js`);
      return res.ok ? res.json() : null;
    }))).filter(Boolean);
  }

  async render() {
    this.products = await this.fetchProducts();
    this.empty.hidden = this.products.length > 0;
    const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    this.grid.innerHTML = this.products.map((p) => {
      const img = p.featured_image
        ? `<img class="card__image" src="${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=900" alt="${esc(p.title)}" loading="lazy">`
        : '';
      const price = p.price_varies ? `${theme.strings.from || 'From'} ${formatMoney(p.price_min)}` : formatMoney(p.price);
      const quickAdd = p.available
        ? `<button type="button" class="card__quick-add" data-quick-add="${p.url}" aria-label="${esc(theme.strings.addToCart)}"><span class="card__quick-add-label">${theme.strings.addToCart}</span><svg class="icon icon-plus" aria-hidden="true" width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 0v11M0 5.5h11" stroke="currentColor" stroke-width="1.2"/></svg></button>`
        : '';
      const remove = this.shared ? '' : `<button type="button" class="card__wishlist-remove" data-wishlist-toggle="${esc(p.handle)}" data-label-add="${esc(theme.strings.wishlistRemove)}" data-label-remove="${esc(theme.strings.wishlistRemove)}">${theme.strings.wishlistRemove}</button>`;
      return `<li class="product-grid__item"><article class="card">
        <a href="${p.url}" class="card__media">${img}</a>
        <div class="card__info">
          <div class="card__text">
            <a href="${p.url}" class="card__title">${esc(p.title)}</a>
            ${p.vendor ? `<div class="card__vendor">${esc(p.vendor)}</div>` : ''}
            <div class="card__price">${price}</div>
            ${remove}
          </div>
          ${quickAdd}
        </div>
      </article></li>`;
    }).join('');
  }

  async addAll() {
    const products = this.products || await this.fetchProducts();
    const items = [];
    let needsOptions = 0;
    products.forEach((p) => {
      const available = p.variants.filter((v) => v.available);
      if (p.variants.length === 1 && available.length) items.push({ id: available[0].id, quantity: 1 });
      else if (available.length) needsOptions += 1;
    });
    if (items.length) {
      await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
    }
    const parts = [];
    if (items.length) parts.push(theme.strings.wishlistAdded.replace('{{ count }}', items.length));
    if (needsOptions) parts.push(theme.strings.wishlistNeedsOptions.replace('{{ count }}', needsOptions));
    if (parts.length) this.say(parts.join(' '));
    if (items.length) await openCart();
  }

  async share() {
    const url = new URL(window.location.href);
    url.searchParams.set('items', wishlist.get().join(','));
    const link = url.toString();
    if (navigator.share) { try { await navigator.share({ url: link }); return; } catch (_) { /* dismissed */ } }
    await navigator.clipboard?.writeText(link);
    this.say(theme.strings.linkCopied);
  }
}
customElements.define('wishlist-page', WishlistPage);

/* Related products & recently viewed ---------------------------------------- */
class CardSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.nav = this.querySelector('[data-slider-pages]');
    if (!this.track || !this.nav) return;
    this.onResize = debounce(() => this.buildPages(), 150);
    window.addEventListener('resize', this.onResize);
    this.track.addEventListener('scroll', debounce(() => this.sync(), 80));
    this.buildPages();
  }

  disconnectedCallback() { window.removeEventListener('resize', this.onResize); }

  get perPage() {
    const first = this.track.children[0];
    if (!first) return 1;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return Math.max(1, Math.floor((this.track.clientWidth + gap) / (first.offsetWidth + gap)));
  }

  buildPages() {
    this.per = this.perPage;
    const pages = Math.ceil(this.track.children.length / this.per);
    this.nav.hidden = pages < 2;
    this.nav.innerHTML = Array.from({ length: pages }, (_, i) => (
      `<button type="button" class="related__page" data-page="${i}">${String(i + 1).padStart(2, '0')}</button>`
    )).join('');
    this.nav.querySelectorAll('[data-page]').forEach((btn) => {
      btn.addEventListener('click', () => this.goTo(Number(btn.dataset.page)));
    });
    this.sync();
  }

  offsetOf(page) {
    const item = this.track.children[page * this.per];
    return item ? item.offsetLeft - this.track.children[0].offsetLeft : 0;
  }

  goTo(page) { this.track.scrollTo({ left: this.offsetOf(page) }); }

  sync() {
    const buttons = [...this.nav.children];
    const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    let active = 0;
    buttons.forEach((_, i) => { if (this.track.scrollLeft >= this.offsetOf(i) - 2) active = i; });
    if (atEnd) active = buttons.length - 1;
    buttons.forEach((btn, i) => btn.setAttribute('aria-current', String(i === active)));
  }
}
customElements.define('card-slider', CardSlider);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      this.load();
    }, { rootMargin: '0px 0px 400px 0px' });
    observer.observe(this);
  }

  async load() {
    const doc = await fetchSection(this.dataset.url, this.dataset.sectionId);
    const fresh = doc.querySelector('product-recommendations');
    if (fresh && fresh.querySelector('[data-slider-track] > *')) this.innerHTML = fresh.innerHTML;
    else this.hidden = true;
  }
}
customElements.define('product-recommendations', ProductRecommendations);


class RecentlyViewed extends HTMLElement {
  async connectedCallback() {
    const limit = Number(this.dataset.limit) || 3;
    const handles = recentlyViewed.get().filter((h) => h !== this.dataset.exclude).slice(0, limit);
    if (!handles.length) return;
    const root = theme.routes.root.replace(/\/$/, '');
    const products = (await Promise.all(handles.map(async (handle) => {
      const res = await fetch(`${root}/products/${handle}.js`);
      return res.ok ? res.json() : null;
    }))).filter(Boolean);
    if (!products.length) return;
    const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    const cardClass = this.dataset.showInfo === 'true' ? 'mini-card' : 'mini-card mini-card--hover';
    this.querySelector('[data-recent-grid]').innerHTML = products.map((p) => {
      const media = p.media && p.media[0];
      const src = media ? (media.preview_image?.src || media.src) : p.featured_image;
      const ratio = media?.preview_image?.aspect_ratio || media?.aspect_ratio;
      const join = src && src.includes('?') ? '&' : '?';
      const img = src
        ? `<img src="${src}${join}width=720" srcset="${src}${join}width=360 360w, ${src}${join}width=720 720w, ${src}${join}width=900 900w" sizes="(min-width: 1024px) 20vw, 50vw" alt="${esc(p.title)}" loading="lazy">`
        : '';
      const price = p.price_varies ? `${theme.strings.from || 'From'} ${formatMoney(p.price_min)}` : formatMoney(p.price);
      return `<li><a href="${p.url}" class="${cardClass}">
        <span class="mini-card__media"${ratio ? ` style="--ratio: ${ratio};"` : ''}>${img}</span>
        <span class="mini-card__info"><span class="mini-card__title">${esc(p.title)}</span><span class="mini-card__price">${price}</span></span>
      </a></li>`;
    }).join('');
    this.hidden = false;
    this.querySelector('card-slider')?.buildPages();
  }
}
customElements.define('recently-viewed', RecentlyViewed);

/* Cookie banner & newsletter popup ------------------------------------------ */
/* Opens/closes a section's popup while it's selected in the theme editor */
const editorPreview = (el, show, hide) => {
  if (!window.Shopify?.designMode) return false;
  const id = el.dataset.sectionId;
  document.addEventListener('shopify:section:select', (e) => { if (e.detail.sectionId === id) show(); });
  document.addEventListener('shopify:section:deselect', (e) => { if (e.detail.sectionId === id) hide(); });
  return true;
};

class CookieBanner extends HTMLElement {
  connectedCallback() {
    this.key = 'eastwood-cookie-choice';
    this.querySelectorAll('[data-cookie-choice]').forEach((btn) => btn.addEventListener('click', () => this.respond(btn.dataset.cookieChoice === 'true')));
    if (editorPreview(this, () => { this.hidden = false; }, () => { this.hidden = true; })) return;
    const decide = () => {
      const api = window.Shopify?.customerPrivacy;
      let stored = null;
      try { stored = localStorage.getItem(this.key); } catch (_) { /* storage off */ }
      if (!api) { this.hidden = stored !== null; return; }
      const everywhere = this.dataset.showEverywhere === 'true';
      const undecided = (api.getTrackingConsent?.() ?? 'no_interaction') === 'no_interaction' && stored === null;
      this.hidden = !(everywhere ? undecided : api.shouldShowBanner?.() && undecided);
    };
    if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => decide());
    } else decide();
  }

  respond(accepted) {
    try { localStorage.setItem(this.key, String(accepted)); } catch (_) { /* storage off */ }
    window.Shopify?.customerPrivacy?.setTrackingConsent?.(
      { analytics: accepted, marketing: accepted, preferences: accepted, sale_of_data: accepted },
      () => {},
    );
    this.hidden = true;
  }
}
customElements.define('cookie-banner', CookieBanner);

class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.key = 'eastwood-newsletter-closed';
    this.subscribedKey = 'eastwood-newsletter-subscribed';
    this.panel = this.querySelector('.newsletter-popup__panel');
    this.addEventListener('click', (e) => { if (e.target.closest('[data-popup-close]')) this.close(); });
    this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    this.querySelector('form')?.addEventListener('submit', () => this.store(this.subscribedKey, '1'));
    if (editorPreview(this, () => this.open(true), () => this.close())) return;

    // Back from submitting: show the thank-you or the error
    const posted = this.querySelector('[data-popup-posted]');
    if (posted) {
      if (posted.dataset.popupPosted === 'error') this.store(this.subscribedKey, null);
      this.open(true);
      return;
    }
    if (this.hasAttribute('data-disabled') || this.read(this.subscribedKey)) return;
    const closedAt = Number(this.read(this.key)) || 0;
    if (Date.now() - closedAt < (Number(this.dataset.days) || 7) * 864e5) return;
    this.timer = setTimeout(() => this.open(), (Number(this.dataset.delay) || 0) * 1000);
  }

  disconnectedCallback() { clearTimeout(this.timer); }

  read(key) { try { return localStorage.getItem(key); } catch (_) { return null; } }

  store(key, value) {
    try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value); } catch (_) { /* storage off */ }
  }

  open(force = false) {
    // Don't interrupt someone using a drawer or the variant selector; try again shortly
    if (!force && document.querySelector('.drawer.is-open, .search-modal.is-open, .configurator.is-open, .gallery-modal:not([hidden])')) {
      this.timer = setTimeout(() => this.open(), 4000);
      return;
    }
    // Sit above the cookie banner when both are in the same corner
    const cookie = document.querySelector('cookie-banner:not([hidden])');
    this.classList.toggle('is-stacked', Boolean(cookie));
    if (cookie) this.style.setProperty('--cookie-h', `${cookie.offsetHeight}px`);
    this.classList.add('is-open');
  }

  close() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    this.store(this.key, String(Date.now()));
  }
}
customElements.define('newsletter-popup', NewsletterPopup);
